import { useState, useEffect, useCallback } from 'react'
import { cuentasWebApi } from '../api/cuentasWeb'
import Boton from '../components/Boton'
import ModalVincularCuentaWeb from '../components/ModalVincularCuentaWeb'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import useDebounce from '../hooks/useDebounce'
import { fmtFecha } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

const REGISTRO = { email: 'Email', google: 'Google' }

/**
 * CLIENTES → Cuentas web: la base que se forma en ligier.com.ar (Martin, 6 sep
 * 2026). Una fila por cuenta del storefront: cómo se registró, si verificó el
 * email, último ingreso, pedidos web y si tiene ficha en LGR. Las sueltas con
 * una ficha del mismo email se vinculan desde acá (el registro solo las ata
 * solo cuando hay exactamente una ficha con ese email); «Vincular a ficha…»
 * ata o cambia la ficha de cualquier cuenta y le carga razones sociales.
 * «Cerrar sesiones» (19 oct 2026) cierra la cuenta en todos los dispositivos
 * donde esté abierta (cuenta comprometida, fraude); la persona vuelve a
 * ingresar con su contraseña. Dos clics: el primero pide confirmar.
 */
export default function CuentasWebPage() {
  const [data, setData] = useState({ data: [], totales: null })
  const [loading, setLoading] = useState(true)
  const [filtros, setFiltros] = useStoredState('lgr.filtros.cuentas-web', { sinFicha: false })
  const [q, setQ] = useState('')
  const qDeb = useDebounce(q, 300)
  const [elegido, setElegido] = useState({})      // cuenta.id → cliente_id elegido entre los candidatos
  const [vinculando, setVinculando] = useState(null)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null)       // cuenta abierta en «Vincular a ficha»
  const [cerrar, setCerrar] = useState(null)     // { id, estado: 'confirmar' | 'cerrando' | 'hecho' }

  // Sin setLoading(true) sincrónico: al cambiar un filtro se muestra lo que
  // había hasta que llega lo nuevo (regla react-hooks/set-state-in-effect).
  const cargar = useCallback(async () => {
    try {
      const { data } = await cuentasWebApi.listar({ sin_ficha: filtros.sinFicha ? 1 : 0, q: qDeb || undefined })
      setData(data)
    } catch { /* transitorio: el próximo refresh lo trae */ } finally { setLoading(false) }
  }, [filtros.sinFicha, qDeb])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 30000)

  const vincular = async (c) => {
    const clienteId = elegido[c.id] ?? c.candidatos[0]?.id
    if (!clienteId) return
    setVinculando(c.id); setError('')
    try {
      await cuentasWebApi.vincular(c.id, clienteId)
      await cargar()
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo vincular.')
    } finally { setVinculando(null) }
  }

  const cerrarSesiones = async (c) => {
    if (cerrar?.id !== c.id || cerrar.estado !== 'confirmar') { setCerrar({ id: c.id, estado: 'confirmar' }); return }
    setCerrar({ id: c.id, estado: 'cerrando' }); setError('')
    try {
      await cuentasWebApi.cerrarSesiones(c.id)
      setCerrar({ id: c.id, estado: 'hecho' })
    } catch (e) {
      setCerrar(null)
      setError(e?.response?.data?.message || 'No se pudieron cerrar las sesiones.')
    }
  }

  const t = data.totales
  const filas = data.data

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Cuentas web</h2>
          <p style={st.sub}>Cuentas creadas en ligier.com.ar. «Sin ficha» = la persona todavía no está atada a un cliente de LGR.</p>
        </div>
      </div>

      {t && (
        <div style={st.kpis}>
          <Kpi n={t.total} label="cuentas" />
          <Kpi n={t.sin_ficha} label="sin ficha" alerta={t.sin_ficha > 0} />
          <Kpi n={t.con_candidato} label="con ficha para vincular" />
          <Kpi n={t.ultimos_30d} label="nuevas en 30 días" />
        </div>
      )}

      <div style={st.filtros}>
        <input
          data-nocaps
          style={st.input}
          placeholder="Buscar por email o nombre"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <label style={st.toggle}>
          <input type="checkbox" checked={!!filtros.sinFicha}
            onChange={() => setFiltros({ ...filtros, sinFicha: !filtros.sinFicha })} />
          Solo sin ficha
        </label>
        <Boton variante={(q || filtros.sinFicha) ? 'secondary' : 'ghost'} sm onClick={() => { setQ(''); setFiltros({ sinFicha: false }) }}>Limpiar</Boton>
      </div>

      {error && <p style={st.error}>{error}</p>}

      {loading ? <p style={st.empty}>Cargando…</p> : filas.length === 0 ? (
        <div style={st.empty}>No hay cuentas con ese filtro.</div>
      ) : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Cuenta</th>
                <th style={st.th}>Registro</th>
                <th style={st.th}>Verificada</th>
                <th style={st.th}>Alta</th>
                <th style={st.th}>Último ingreso</th>
                <th style={st.th}>Pedidos web</th>
                <th style={st.th}>Ficha LGR</th>
                <th style={st.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {filas.map(c => (
                <tr key={c.id} style={st.tr}
                  onMouseEnter={e => (e.currentTarget.style.background = colors.bgSoft)}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <td style={st.td}>
                    <div style={st.principal}>{c.email}</div>
                    {c.nombre && <div style={st.subLinea}>{c.nombre}</div>}
                  </td>
                  <td style={st.tdSec}>{REGISTRO[c.proveedor] || c.proveedor}</td>
                  <td style={st.td}>{c.verificada ? <span style={st.ok}>✓</span> : <span style={st.plh}>—</span>}</td>
                  <td style={st.tdMono}>{fmtFecha(c.creada_at)}</td>
                  <td style={st.tdMono}>{c.ultimo_login_at ? fmtFecha(c.ultimo_login_at) : <span style={st.meta}>—</span>}</td>
                  <td style={st.td}>
                    {c.pedidos ? (
                      <>
                        <div style={st.principal}>{c.pedidos.n}</div>
                        <div style={{ ...st.subLinea, fontFamily: font.mono }}>{c.pedidos.ultimo_numero} · {fmtFecha(c.pedidos.ultimo)}</div>
                      </>
                    ) : <span style={st.meta}>—</span>}
                  </td>
                  <td style={st.td}>
                    {c.cliente ? (
                      <>
                        <div style={st.principalCorto} title={c.cliente.nombre}>{c.cliente.nombre}</div>
                        <div style={st.subLinea}>{c.cliente.codigo_presea ? <span style={sx.mono}>{c.cliente.codigo_presea}</span> : 'pendiente'}{!c.cliente.activo && ' · inactivo'}</div>
                      </>
                    ) : <span style={st.sinFicha}>sin ficha</span>}
                  </td>
                  <td style={st.td} onClick={e => e.stopPropagation()}>
                    <div style={st.accion}>
                    {c.cliente || c.candidatos.length === 0 ? (
                      <Boton variante="ghost" sm onClick={() => setModal(c)}>{c.cliente ? 'Cambiar ficha…' : 'Vincular a ficha…'}</Boton>
                    ) : (
                      <div style={st.accion}>
                        {c.candidatos.length > 1 && (
                          <select style={st.select} value={elegido[c.id] ?? c.candidatos[0].id}
                            onChange={e => setElegido({ ...elegido, [c.id]: Number(e.target.value) })}>
                            {c.candidatos.map(k => (
                              <option key={k.id} value={k.id}>{k.nombre} ({k.codigo_presea || 'pendiente'}{k.activo ? '' : ', inactivo'})</option>
                            ))}
                          </select>
                        )}
                        {c.candidatos.length === 1 && (
                          <span style={st.candidato} title={c.candidatos[0].nombre}>{c.candidatos[0].nombre} {c.candidatos[0].codigo_presea ? <span style={sx.mono}>{c.candidatos[0].codigo_presea}</span> : '(pendiente)'}</span>
                        )}
                        <div style={st.botonesFila}>
                          <Boton variante="secondary" sm disabled={vinculando === c.id} onClick={() => vincular(c)}>
                            Vincular
                          </Boton>
                          <Boton variante="ghost" sm onClick={() => setModal(c)}>Otra ficha…</Boton>
                        </div>
                      </div>
                    )}
                    {cerrar?.id === c.id && cerrar.estado === 'hecho' ? (
                      <span style={st.hecho}>Sesiones cerradas</span>
                    ) : (
                      <Boton variante="ghost" sm disabled={cerrar?.id === c.id && cerrar.estado === 'cerrando'}
                        title="Cierra la cuenta en todos los dispositivos donde esté abierta en ligier.com.ar"
                        onClick={() => cerrarSesiones(c)}>
                        {cerrar?.id === c.id && cerrar.estado === 'confirmar' ? '¿Confirmar? Cerrar sesiones' : 'Cerrar sesiones'}
                      </Boton>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <ModalVincularCuentaWeb cuenta={modal} onClose={() => setModal(null)} onSaved={cargar} />}
    </div>
  )
}

function Kpi({ n, label, alerta }) {
  return (
    <div style={st.kpi}>
      <span style={{ ...st.kpiNum, ...(alerta ? st.kpiAlerta : {}) }}>{n}</span>
      <span style={st.kpiLabel}>{label}</span>
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.lg, flexWrap: 'wrap', gap: space.lg },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: '2px 0 0', fontSize: text.sec, color: colors.textSec },
  kpis: { display: 'flex', gap: space.xl, flexWrap: 'wrap', background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg },
  kpi: { display: 'flex', flexDirection: 'column', gap: 1 },
  kpiNum: { fontSize: 20, fontWeight: 600, color: colors.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 },
  kpiAlerta: { color: colors.warn },
  kpiLabel: { fontSize: text.meta, color: colors.textMeta, whiteSpace: 'nowrap' },
  filtros: { ...sx.filterBar, marginBottom: space.lg },
  input: { ...sx.input, minWidth: 260 },
  toggle: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: text.sec, color: colors.textSec, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
  error: { margin: `0 0 ${space.md}px`, fontSize: text.sec, color: colors.err },
  empty: { background: colors.bg, borderRadius: radius.md, padding: 40, textAlign: 'center', color: colors.textMeta, border: `1px solid ${colors.border}` },
  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 14px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 52 },
  td: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.3 },
  tdSec: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, lineHeight: 1.3 },
  tdMono: { padding: '8px 14px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  principalCorto: { fontSize: text.body, fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' },
  subLinea: { fontSize: text.sec, color: colors.textMeta, marginTop: 1 },
  meta: { fontSize: text.sec, color: colors.textMeta },
  ok: { color: colors.ok },
  plh: { fontSize: text.sec, color: colors.textPlh },
  sinFicha: { display: 'inline-block', fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.warn, background: colors.warnBg, padding: '2px 8px', borderRadius: radius.sm, whiteSpace: 'nowrap' },
  accion: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 },
  botonesFila: { display: 'flex', gap: 4 },
  hecho: { fontSize: text.sec, color: colors.ok, whiteSpace: 'nowrap' },
  candidato: { fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' },
  select: { padding: '4px 8px', fontSize: text.sec, border: `1px solid ${colors.border}`, borderRadius: radius.sm, maxWidth: 220, fontFamily: font.sans, color: colors.text },
}
